import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router, RouterLink } from '@angular/router';
import { AvatarPipe } from 'app/core/pipes/avatar.pipe';
import { CallCenterPortalService } from 'app/core/services/call-center-portal.service';
import { SnagPortalService } from 'app/core/services/snag-portal.service';
import { SweetAlertService } from 'app/core/services/sweet-alert.service';
import { AuthService } from 'app/modules/auth/auth.service';
import { lastValueFrom } from 'rxjs';
import Swal from 'sweetalert2';

@Component({
  selector: 'user',
  templateUrl: './user.component.html',
  styleUrl: './user.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'user',
  standalone: true,
  imports: [MatButtonModule, MatMenuModule, MatIconModule, NgClass, MatDividerModule, MatTooltipModule, RouterLink, AvatarPipe],
})
export class UserComponent {
  loading: number = 0;

  get profile(): any {
    return this.authService.profile();
  }

  constructor(
    private authService: AuthService,
    private router: Router,
    private snagPortalService: SnagPortalService,
    private callCenterPortalService: CallCenterPortalService,
    private sweetAlert: SweetAlertService
  ) {}

  logout(): void {
    this.loading++;
    this.authService.logout().subscribe({
      next: () => {
        this.authService.profile.set(null);
        this.router.navigate(['/login']);
        this.loading--;
      },
      error: () => {
        this.loading--;
      },
    });
  }

  public OpenSnagsPortal(): void {
    this.sweetAlert
      .confirmationAsync(
        'You will be redirected to the Snags Portal in a new tab.',
        async () => {
          try {
            const response: any = await lastValueFrom(this.snagPortalService.getUrl());
            return response.record.url;
          } catch (error: any) {
            Swal.showValidationMessage(error?.error?.message ?? 'Failed to retrieve Snags Portal URL.');
          }
        },
        { title: 'Open Snags Portal', confirmButtonText: 'Open' }
      )
      .then((result) => {
        if (result.isConfirmed && result.value) {
          window.open(result.value, '_blank');
        }
      });
  }

  public OpenCallCenterPortal(): void {
    if (!this.profile?.call_center_agent?.status) {
      this.sweetAlert.error('Your call center agent account is disabled. Please contact your administrator.');
      return;
    }

    this.sweetAlert
      .confirmationAsync(
        'You will be redirected to the Call Center Portal in a new tab.',
        async () => {
          try {
            const response: any = await lastValueFrom(this.callCenterPortalService.getUrl());
            return response.record.url;
          } catch (error: any) {
            Swal.showValidationMessage(error?.error?.message ?? 'Failed to retrieve Call Center Portal URL.');
          }
        },
        { title: 'Open Call Center Portal', confirmButtonText: 'Open' }
      )
      .then((result) => {
        if (result.isConfirmed && result.value) {
          window.open(result.value, '_blank');
        }
      });
  }
}
